/**
 * `window.__game` (docs/spec/05 §5): enough to drive and inspect the game from a console or a
 * Playwright test without a hand on the keyboard.
 */

import type { Controls } from '../input/controls';
import type { GameState } from '../sim';
import type { MenuAction, Route } from '../ui/scene';
import { GameScene } from './game/gameScene';
import type { App } from './host';

export interface DebugApi {
  route(): Route | null;
  go(route: Route): void;
  action(a: MenuAction): void;
  /** Hold these until changed. */
  hold(keys: { left?: boolean; right?: boolean; fire?: boolean; bomb?: boolean }): void;
  /** Run `ms` of frames at once (a background tab gets no animation frames). */
  advance(ms: number): void;
  /** Stop (or restart) real time: frozen, only `advance` moves the game. */
  freeze(on: boolean): void;
  /** A summary of the running game, when one is on screen. */
  state(): unknown;
  /** Give the stick to the autopilot, or take it back. */
  bot(on: boolean): void;
  /** The engine's own state, to look at or to set a scene up by hand. */
  sim(): GameState | null;
}

declare global {
  interface Window {
    __game?: DebugApi;
  }
}

export function installDebugApi(app: App, controls: Controls): void {
  const game = (): GameScene | null => (app.scene instanceof GameScene ? app.scene : null);
  window.__game = {
    route: () => app.route,
    go: (r) => app.go(r),
    action: (a) => app.scene?.action?.(a),
    hold(keys) {
      controls.keys.release('debug');
      for (const k of ['left', 'right', 'fire', 'bomb'] as const) if (keys[k] === true) controls.keys.set('debug', k, true);
    },
    advance(ms) {
      for (let t = 0; t < ms; t += 16) app.frame(16);
    },
    freeze(on) {
      app.frozen = on;
    },
    state: () => game()?.debugState() ?? null,
    bot: (on) => game()?.setBot(on),
    sim: () => game()?.state ?? null,
  };
}
